import { useState, useEffect } from "react";
import { Save, Loader2, Building, CreditCard, Eye, EyeOff, ToggleLeft, ToggleRight, Zap, MessageSquare, Wallet } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import API from "@/lib/api";
import { toast } from "sonner";

export default function AdminSettings() {
  const [tab, setTab] = useState("bank");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [showFlwSecret, setShowFlwSecret] = useState(false);

  const [bankForm, setBankForm] = useState({ bank_name: "", account_name: "", account_number: "", instructions: "" });
  const [paystackForm, setPaystackForm] = useState({ enabled: false, public_key: "", secret_key: "" });
  const [secretConfigured, setSecretConfigured] = useState(false);
  const [flwForm, setFlwForm] = useState({ enabled: false, public_key: "", secret_key: "", webhook_hash: "" });
  const [flwSecretConfigured, setFlwSecretConfigured] = useState(false);
  const [flwHashConfigured, setFlwHashConfigured] = useState(false);
  const [topupForm, setTopupForm] = useState({
    price_per_100_credits: "550",
    transaction_fee_percent: "1.5",
    min_credits: "100",
    max_credits: "100000",
  });
  const [smsPricingForm, setSmsPricingForm] = useState({ credits_per_segment: "1" });

  useEffect(() => { loadSettings(); }, []);

  const loadSettings = async () => {
    try {
      const [bd, gs, ts, sp] = await Promise.all([
        API.get("/payments/bank-details"),
        API.get("/payments/gateway-settings"),
        API.get("/payments/topup-settings"),
        API.get("/admin/sms-pricing"),
      ]);
      if (bd.data) setBankForm({
        bank_name: bd.data.bank_name || "",
        account_name: bd.data.account_name || "",
        account_number: bd.data.account_number || "",
        instructions: bd.data.instructions || "",
      });
      const ps = gs.data?.paystack || {};
      setPaystackForm({ enabled: ps.enabled || false, public_key: ps.public_key || "", secret_key: "" });
      setSecretConfigured(ps.secret_key_configured || false);
      const flw = gs.data?.flutterwave || {};
      setFlwForm({ enabled: flw.enabled || false, public_key: flw.public_key || "", secret_key: "", webhook_hash: "" });
      setFlwSecretConfigured(flw.secret_key_configured || false);
      setFlwHashConfigured(flw.webhook_hash_configured || false);
      if (ts.data) setTopupForm({
        price_per_100_credits: String((ts.data.price_per_100_credits || 55000) / 100),
        transaction_fee_percent: String(ts.data.transaction_fee_percent ?? 1.5),
        min_credits: String(ts.data.min_credits || 100),
        max_credits: String(ts.data.max_credits || 100000),
      });
      if (sp.data) setSmsPricingForm({ credits_per_segment: String(sp.data.credits_per_segment || 1) });
    } catch {}
    finally { setLoading(false); }
  };

  const saveBankDetails = async () => {
    setSaving(true);
    try {
      await API.put("/payments/bank-details", bankForm);
      toast.success("Bank details updated");
    } catch { toast.error("Failed to save bank details"); }
    finally { setSaving(false); }
  };

  const savePaystack = async () => {
    if (paystackForm.enabled && !paystackForm.public_key) { toast.error("Public key is required to enable Paystack"); return; }
    setSaving(true);
    try {
      const payload = { paystack: { enabled: paystackForm.enabled, public_key: paystackForm.public_key } };
      if (paystackForm.secret_key) payload.paystack.secret_key = paystackForm.secret_key;
      await API.put("/payments/gateway-settings", payload);
      toast.success("Paystack settings saved");
      setPaystackForm((f) => ({ ...f, secret_key: "" }));
      setSecretConfigured(!!paystackForm.secret_key || secretConfigured);
    } catch { toast.error("Failed to save Paystack settings"); }
    finally { setSaving(false); }
  };

  const saveFlutterwave = async () => {
    if (flwForm.enabled && !flwForm.public_key) { toast.error("Public key is required to enable Flutterwave"); return; }
    setSaving(true);
    try {
      const payload = { flutterwave: { enabled: flwForm.enabled, public_key: flwForm.public_key } };
      if (flwForm.secret_key) payload.flutterwave.secret_key = flwForm.secret_key;
      if (flwForm.webhook_hash) payload.flutterwave.webhook_hash = flwForm.webhook_hash;
      await API.put("/payments/gateway-settings", payload);
      toast.success("Flutterwave settings saved");
      setFlwSecretConfigured(!!(flwForm.secret_key || flwSecretConfigured));
      setFlwHashConfigured(!!(flwForm.webhook_hash || flwHashConfigured));
      setFlwForm((f) => ({ ...f, secret_key: "", webhook_hash: "" }));
    } catch { toast.error("Failed to save Flutterwave settings"); }
    finally { setSaving(false); }
  };

  const saveTopup = async () => {
    const price = parseFloat(topupForm.price_per_100_credits);
    const fee = parseFloat(topupForm.transaction_fee_percent);
    if (isNaN(price) || price <= 0) { toast.error("Enter a valid price per 100 credits (₦)"); return; }
    if (isNaN(fee) || fee < 0 || fee > 10) { toast.error("Transaction fee must be between 0% and 10%"); return; }
    setSaving(true);
    try {
      await API.put("/payments/topup-settings", {
        price_per_100_credits: Math.round(price * 100),  // convert ₦ → kobo
        transaction_fee_percent: fee,
        min_credits: parseInt(topupForm.min_credits) || 100,
        max_credits: parseInt(topupForm.max_credits) || 100000,
      });
      toast.success("Top-up pricing updated");
    } catch { toast.error("Failed to save top-up settings"); }
    finally { setSaving(false); }
  };

  const saveSmsPricing = async () => {
    const cps = parseFloat(smsPricingForm.credits_per_segment);
    if (isNaN(cps) || cps <= 0) { toast.error("Credits per segment must be a positive number"); return; }
    setSaving(true);
    try {
      await API.put("/admin/sms-pricing", { credits_per_segment: cps });
      toast.success("SMS pricing updated");
    } catch { toast.error("Failed to save SMS pricing"); }
    finally { setSaving(false); }
  };

  const TABS = [
    { id: "bank", label: "Bank Transfer" },
    { id: "paystack", label: "Paystack" },
    { id: "flutterwave", label: "Flutterwave" },
    { id: "topup", label: "Top-Up Pricing" },
    { id: "sms", label: "SMS Pricing" },
    { id: "general", label: "General" },
  ];

  const pricePreview = parseFloat(topupForm.price_per_100_credits) || 0;

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-extrabold text-slate-900">Settings</h1>
        <p className="text-sm text-slate-500 mt-1">Configure platform settings and payment gateways</p>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-1 bg-slate-100 rounded-lg p-1 w-fit">
        {TABS.map(({ id, label }) => (
          <button key={id} onClick={() => setTab(id)}
            className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${tab === id ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"}`}
            data-testid={`settings-tab-${id}`}>
            {label}
          </button>
        ))}
      </div>

      {/* ── Bank Transfer ── */}
      {tab === "bank" && (
        <div className="bg-white border border-slate-200 rounded-xl p-6 max-w-lg shadow-sm">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-9 h-9 bg-[#eef1f8] border border-[#c0cfe8] rounded-xl flex items-center justify-center">
              <CreditCard size={18} className="text-[#0c1e41]" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-slate-900">Bank Transfer Details</h3>
              <p className="text-xs text-slate-500">Shown to customers when paying via bank transfer</p>
            </div>
          </div>
          <div className="space-y-4">
            {[["bank_name","Bank Name","GTBank"],["account_name","Account Name","Sendora"],["account_number","Account Number","0123456789"]].map(([k,l,p]) => (
              <div key={k} className="space-y-2">
                <Label className="text-slate-700 text-sm">{l}</Label>
                <Input value={bankForm[k]} onChange={(e) => setBankForm({ ...bankForm, [k]: e.target.value })} placeholder={p} className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-400 h-10" data-testid={`bank-${k}`} />
              </div>
            ))}
            <div className="space-y-2">
              <Label className="text-slate-700 text-sm">Transfer Instructions</Label>
              <textarea value={bankForm.instructions} onChange={(e) => setBankForm({ ...bankForm, instructions: e.target.value })} placeholder="Use your email as payment reference/narration..." rows={3} className="w-full bg-white border border-slate-300 text-slate-900 text-sm rounded-md px-3 py-2 resize-none focus:outline-none focus:border-[#0c1e41] placeholder:text-slate-400" />
            </div>
            <Button onClick={saveBankDetails} disabled={saving} className="bg-[#0c1e41] hover:bg-[#091629] shadow-sm" data-testid="save-bank-btn">
              {saving ? <Loader2 size={14} className="animate-spin mr-2" /> : <Save size={14} className="mr-2" />}
              Save Bank Details
            </Button>
          </div>
        </div>
      )}

      {/* ── Paystack ── */}
      {tab === "paystack" && (
        <div className="bg-white border border-slate-200 rounded-xl p-6 max-w-lg shadow-sm">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-9 h-9 bg-[#eef1f8] border border-[#c0cfe8] rounded-xl flex items-center justify-center">
              <CreditCard size={18} className="text-[#0c1e41]" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-slate-900">Paystack Configuration</h3>
              <p className="text-xs text-slate-500">Enable card payments via Paystack for Nigerian customers</p>
            </div>
          </div>
          <div className="space-y-5">
            <div className="flex items-center justify-between py-3 px-4 bg-slate-50 border border-slate-200 rounded-lg">
              <div>
                <p className="text-sm font-medium text-slate-900">Enable Paystack</p>
                <p className="text-xs text-slate-500 mt-0.5">Allow customers to pay with Paystack</p>
              </div>
              <button onClick={() => setPaystackForm((f) => ({ ...f, enabled: !f.enabled }))}
                className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full transition-all ${paystackForm.enabled ? "bg-emerald-100 text-emerald-700 border border-emerald-200" : "bg-slate-200 text-slate-500"}`}
                data-testid="paystack-toggle">
                {paystackForm.enabled ? <ToggleRight size={16} /> : <ToggleLeft size={16} />}
                {paystackForm.enabled ? "Enabled" : "Disabled"}
              </button>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-700 text-sm">Public Key</Label>
              <Input value={paystackForm.public_key} onChange={(e) => setPaystackForm({ ...paystackForm, public_key: e.target.value })} placeholder="pk_live_... or pk_test_..." className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-400 h-10 font-mono text-sm" data-testid="paystack-public-key" />
              <p className="text-xs text-slate-400">Used on the frontend to initialize payments</p>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-700 text-sm">
                Secret Key {secretConfigured && <span className="text-xs text-emerald-600 font-normal ml-2">(configured — enter new value to update)</span>}
              </Label>
              <div className="relative">
                <Input type={showSecret ? "text" : "password"} value={paystackForm.secret_key} onChange={(e) => setPaystackForm({ ...paystackForm, secret_key: e.target.value })} placeholder={secretConfigured ? "Enter new key to update..." : "sk_live_... or sk_test_..."} className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-400 h-10 font-mono text-sm pr-10" data-testid="paystack-secret-key" />
                <button type="button" onClick={() => setShowSecret(!showSecret)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  {showSecret ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
              <p className="text-xs text-slate-400">Never shared with the browser — stored server-side only</p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-700">
              <strong>Webhook URL:</strong> Set this in your Paystack dashboard → Settings → Webhooks:<br />
              <span className="font-mono mt-1 block">{window.location.origin}/api/payments/paystack/webhook</span>
            </div>
            <Button onClick={savePaystack} disabled={saving} className="bg-[#0c1e41] hover:bg-[#091629] shadow-sm" data-testid="save-paystack-btn">
              {saving ? <Loader2 size={14} className="animate-spin mr-2" /> : <Save size={14} className="mr-2" />}
              Save Paystack Settings
            </Button>
          </div>
        </div>
      )}

      {/* ── Flutterwave ── */}
      {tab === "flutterwave" && (
        <div className="bg-white border border-slate-200 rounded-xl p-6 max-w-lg shadow-sm">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-9 h-9 bg-[#eef1f8] border border-[#c0cfe8] rounded-xl flex items-center justify-center">
              <Wallet size={18} className="text-[#0c1e41]" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-slate-900">Flutterwave Configuration</h3>
              <p className="text-xs text-slate-500">Enable card, bank & mobile money payments via Flutterwave</p>
            </div>
          </div>
          <div className="space-y-5">
            <div className="flex items-center justify-between py-3 px-4 bg-slate-50 border border-slate-200 rounded-lg">
              <div>
                <p className="text-sm font-medium text-slate-900">Enable Flutterwave</p>
                <p className="text-xs text-slate-500 mt-0.5">Allow customers to pay with Flutterwave</p>
              </div>
              <button onClick={() => setFlwForm((f) => ({ ...f, enabled: !f.enabled }))}
                className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full transition-all ${flwForm.enabled ? "bg-emerald-100 text-emerald-700 border border-emerald-200" : "bg-slate-200 text-slate-500"}`}
                data-testid="flutterwave-toggle">
                {flwForm.enabled ? <ToggleRight size={16} /> : <ToggleLeft size={16} />}
                {flwForm.enabled ? "Enabled" : "Disabled"}
              </button>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-700 text-sm">Public Key</Label>
              <Input value={flwForm.public_key} onChange={(e) => setFlwForm({ ...flwForm, public_key: e.target.value })} placeholder="FLWPUBK_TEST-... or FLWPUBK-..." className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-400 h-10 font-mono text-sm" data-testid="flutterwave-public-key" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-700 text-sm">
                Secret Key {flwSecretConfigured && <span className="text-xs text-emerald-600 font-normal ml-2">(configured — enter new value to update)</span>}
              </Label>
              <div className="relative">
                <Input type={showFlwSecret ? "text" : "password"} value={flwForm.secret_key} onChange={(e) => setFlwForm({ ...flwForm, secret_key: e.target.value })} placeholder={flwSecretConfigured ? "Enter new key to update..." : "FLWSECK_TEST-... or FLWSECK-..."} className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-400 h-10 font-mono text-sm pr-10" data-testid="flutterwave-secret-key" />
                <button type="button" onClick={() => setShowFlwSecret(!showFlwSecret)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  {showFlwSecret ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
              <p className="text-xs text-slate-400">Never shared with the browser — stored server-side only</p>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-700 text-sm">
                Webhook Hash (Secret) {flwHashConfigured && <span className="text-xs text-emerald-600 font-normal ml-2">(configured)</span>}
              </Label>
              <Input type="password" value={flwForm.webhook_hash} onChange={(e) => setFlwForm({ ...flwForm, webhook_hash: e.target.value })} placeholder={flwHashConfigured ? "Enter new hash to update..." : "Your Flutterwave webhook secret hash"} className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-400 h-10 font-mono text-sm" data-testid="flutterwave-webhook-hash" />
              <p className="text-xs text-slate-400">Set a secret hash in Flutterwave dashboard. We verify incoming webhooks against this value.</p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-700">
              <strong>Webhook URL:</strong> Set this in your Flutterwave dashboard → Settings → Webhooks:<br />
              <span className="font-mono mt-1 block">{window.location.origin}/api/payments/flutterwave/webhook</span>
            </div>
            <Button onClick={saveFlutterwave} disabled={saving} className="bg-[#0c1e41] hover:bg-[#091629] shadow-sm" data-testid="save-flutterwave-btn">
              {saving ? <Loader2 size={14} className="animate-spin mr-2" /> : <Save size={14} className="mr-2" />}
              Save Flutterwave Settings
            </Button>
          </div>
        </div>
      )}

      {/* ── Top-Up Pricing ── */}
      {tab === "topup" && (
        <div className="bg-white border border-slate-200 rounded-xl p-6 max-w-lg shadow-sm">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-9 h-9 bg-[#eef1f8] border border-[#c0cfe8] rounded-xl flex items-center justify-center">
              <Zap size={18} className="text-[#0c1e41]" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-slate-900">Top-Up Credit Pricing</h3>
              <p className="text-xs text-slate-500">Set the unit price customers pay when topping up credits</p>
            </div>
          </div>
          <div className="space-y-5">

            {/* Live preview */}
            <div className="bg-[#eef1f8] border border-[#c0cfe8] rounded-xl p-4">
              <p className="text-xs font-bold text-[#0c1e41] mb-2">Pricing Preview</p>
              <div className="grid grid-cols-2 gap-2 text-xs text-slate-700">
                <span>100 credits</span>
                <span className="font-semibold text-right">₦{pricePreview.toLocaleString()}</span>
                <span>500 credits</span>
                <span className="font-semibold text-right">₦{(pricePreview * 5).toLocaleString()}</span>
                <span>1,000 credits</span>
                <span className="font-semibold text-right">₦{(pricePreview * 10).toLocaleString()}</span>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-slate-700 text-sm font-semibold">Price per 100 Credits (₦)</Label>
              <Input type="number" value={topupForm.price_per_100_credits} onChange={(e) => setTopupForm({ ...topupForm, price_per_100_credits: e.target.value })} placeholder="550" className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-400 h-10" data-testid="topup-price-input" />
              <p className="text-xs text-slate-400">e.g. ₦550 = customer pays ₦5.50 per credit</p>
            </div>

            <div className="space-y-2">
              <Label className="text-slate-700 text-sm font-semibold">Paystack Transaction Fee (%)</Label>
              <Input type="number" step="0.1" min="0" max="10" value={topupForm.transaction_fee_percent} onChange={(e) => setTopupForm({ ...topupForm, transaction_fee_percent: e.target.value })} placeholder="1.5" className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-400 h-10" data-testid="topup-fee-input" />
              <p className="text-xs text-slate-400">Added only for Paystack payments; bank transfer is always free</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-700 text-sm">Min Top-Up (credits)</Label>
                <Input type="number" value={topupForm.min_credits} onChange={(e) => setTopupForm({ ...topupForm, min_credits: e.target.value })} placeholder="100" className="bg-white border-slate-300 text-slate-900 h-10" data-testid="topup-min-credits" />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-700 text-sm">Max Top-Up (credits)</Label>
                <Input type="number" value={topupForm.max_credits} onChange={(e) => setTopupForm({ ...topupForm, max_credits: e.target.value })} placeholder="100000" className="bg-white border-slate-300 text-slate-900 h-10" data-testid="topup-max-credits" />
              </div>
            </div>

            <Button onClick={saveTopup} disabled={saving} className="bg-[#0c1e41] hover:bg-[#091629] shadow-sm" data-testid="save-topup-btn">
              {saving ? <Loader2 size={14} className="animate-spin mr-2" /> : <Save size={14} className="mr-2" />}
              Save Top-Up Pricing
            </Button>
          </div>
        </div>
      )}

      {/* ── SMS Pricing ── */}
      {tab === "sms" && (
        <div className="bg-white border border-slate-200 rounded-xl p-6 max-w-lg shadow-sm">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-9 h-9 bg-[#eef1f8] border border-[#c0cfe8] rounded-xl flex items-center justify-center">
              <MessageSquare size={18} className="text-[#0c1e41]" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-slate-900">SMS Pricing</h3>
              <p className="text-xs text-slate-500">Set how many credits are deducted per SMS segment sent</p>
            </div>
          </div>
          <div className="space-y-5">
            <div className="bg-[#eef1f8] border border-[#c0cfe8] rounded-xl p-4">
              <p className="text-xs font-bold text-[#0c1e41] mb-2">How SMS credits work</p>
              <ul className="text-xs text-slate-600 space-y-1 list-disc list-inside">
                <li>A standard SMS = 1 segment (up to 160 chars GSM-7)</li>
                <li>Longer messages split into multiple segments</li>
                <li>Set credits per segment to control cost per SMS</li>
              </ul>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-700 text-sm font-semibold">Credits per SMS Segment</Label>
              <Input
                type="number"
                min="1"
                step="1"
                value={smsPricingForm.credits_per_segment}
                onChange={(e) => setSmsPricingForm({ credits_per_segment: e.target.value })}
                placeholder="1"
                className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-400 h-10"
                data-testid="sms-credits-per-segment"
              />
              <p className="text-xs text-slate-400">Default: 1 credit per segment. Increase to charge more per SMS.</p>
            </div>
            <Button onClick={saveSmsPricing} disabled={saving} className="bg-[#0c1e41] hover:bg-[#091629] shadow-sm" data-testid="save-sms-pricing-btn">
              {saving ? <Loader2 size={14} className="animate-spin mr-2" /> : <Save size={14} className="mr-2" />}
              Save SMS Pricing
            </Button>
          </div>
        </div>
      )}

      {/* ── General ── */}
      {tab === "general" && (
        <div className="bg-white border border-slate-200 rounded-xl p-6 max-w-lg shadow-sm">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-9 h-9 bg-slate-100 border border-slate-200 rounded-xl flex items-center justify-center">
              <Building size={18} className="text-slate-600" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-slate-900">Platform Settings</h3>
              <p className="text-xs text-slate-500">General configuration</p>
            </div>
          </div>
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-sm text-slate-500">
            More settings coming soon — currency, SMS pricing, moderation rules, referral programs, and feature flags.
          </div>
        </div>
      )}
    </div>
  );
}
